/**
 * Context Awareness Engine
 * 
 * Multi-dimensional contextual intelligence that understands WHY an action matters:
 * 1. Temporal Context - Time-based factors (deadlines, time of day, day of week)
 * 2. Relational Context - Relationship dynamics with contacts
 * 3. Project Context - Active projects and their status
 * 4. Behavioral Context - User's current state and workload
 * 5. Environmental Context - Company events, industry news, market conditions
 */

import { blink } from '@/lib/blink';
import { selfLearningMemory } from './selfLearningMemory';



/**
 * Temporal context analysis
 */
interface TemporalContext {
  currentTime: Date;
  dayOfWeek: string;
  timeOfDay: 'early_morning' | 'morning' | 'afternoon' | 'evening' | 'night';
  isWorkingHours: boolean;
  daysUntilDeadline?: number;
  urgencyMultiplier: number;
  suggestedTone: string;
  shouldDefer: boolean;
  deferUntil?: Date;
}

/**
 * Relational context for contacts
 */
interface RelationalContext {
  contactEmail: string;
  relationship: {
    strength: number; // 0-1
    hierarchy: 'superior' | 'peer' | 'subordinate' | 'client' | 'vendor';
    communicationFrequency: number; // per week
    responseExpectation: number; // hours
    lastInteraction: Date;
    sentimentTrend: 'positive' | 'neutral' | 'negative';
    sharedProjects: string[];
  };
  adaptiveBehavior: {
    tone: string;
    meetingPriority: 'critical' | 'high' | 'medium' | 'low';
    autoApproveReschedule: boolean;
    notificationPreference: 'immediate' | 'batched' | 'quiet';
  };
}

/**
 * Project context tracking
 */
interface ProjectContext {
  activeProjects: Array<{
    id: string;
    name: string;
    status: 'on_track' | 'at_risk' | 'blocked' | 'completed';
    deadline: Date;
    dependencies: string[];
    criticalPath: boolean;
    healthScore: number; // 0-100
  }>;
  projectLinks: Record<string, string[]>; // email/meeting ID -> project IDs
  priorityQueue: Array<{
    projectId: string;
    action: string;
    dueBy: Date;
    blocking: string[];
  }>;
}

/**
 * Behavioral context - user's current state
 */
interface BehavioralContext {
  currentState: {
    workload: number; // 0-100%
    meetingFatigue: number; // 0-100
    focusTimeDeficit: number; // minutes needed
    energyLevel: 'high' | 'medium' | 'low';
    stressLevel: 'low' | 'medium' | 'high';
    nextNDays: {
      days: number;
      hoursOvercommitted: number;
      conflictsCount: number;
    };
  };
  protectiveActions: string[];
  recommendations: string[];
}

/**
 * Environmental context
 */
interface EnvironmentalContext {
  companyEvents: Array<{
    type: string;
    date: Date;
    impact: 'high' | 'medium' | 'low';
  }>;
  industryNews: string[];
  marketConditions: string[];
  seasonalFactors: string[];
}

/**
 * Complete contextual assessment
 */
interface ContextualAssessment {
  temporal: TemporalContext;
  relational?: RelationalContext;
  project?: ProjectContext;
  behavioral: BehavioralContext;
  environmental: EnvironmentalContext;
  overallRecommendation: {
    shouldProceed: boolean;
    confidence: number;
    reasoning: string;
    alternatives: string[];
  };
}

export class ContextAwarenessEngine {
  private userId: string | null = null;

  async initialize(userId: string): Promise<void> {
    this.userId = userId;
    await selfLearningMemory.initialize(userId);
  }

  /**
   * Analyze temporal context for an action
   */
  async analyzeTemporalContext(
    deadline?: Date,
    isUrgent: boolean = false
  ): Promise<TemporalContext> {
    const now = new Date();
    const hour = now.getHours();
    const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

    // Get user's working patterns
    const semantic = await selfLearningMemory.getSemanticMemory();
    const workingHours = semantic.workingPatterns.typicalWorkHours;

    let timeOfDay: TemporalContext['timeOfDay'] = 'morning';
    if (hour < 6) timeOfDay = 'night';
    else if (hour < 9) timeOfDay = 'early_morning';
    else if (hour < 12) timeOfDay = 'morning';
    else if (hour < 17) timeOfDay = 'afternoon';
    else if (hour < 22) timeOfDay = 'evening';
    else timeOfDay = 'night';

    const isWorkingHours = hour >= workingHours.start && hour < workingHours.end;
    
    let urgencyMultiplier = 1.0;
    let suggestedTone = 'professional';
    let shouldDefer = false;
    let deferUntil: Date | undefined;

    // Adjust for time of day
    if (timeOfDay === 'evening' || timeOfDay === 'night') {
      urgencyMultiplier *= 0.5;
      suggestedTone = 'friendly, non-work';
      if (!isUrgent) {
        shouldDefer = true;
        deferUntil = new Date(now);
        deferUntil.setDate(deferUntil.getDate() + 1);
        deferUntil.setHours(workingHours.start, 0, 0, 0);
      }
    }

    // Friday afternoon protection
    if (dayOfWeek === 'friday' && hour > 14 && !isUrgent) {
      urgencyMultiplier *= 0.3;
      shouldDefer = true;
      deferUntil = new Date(now);
      deferUntil.setDate(deferUntil.getDate() + ((8 - deferUntil.getDay()) % 7)); // Next Monday
      deferUntil.setHours(workingHours.start, 0, 0, 0);
    }

    // Deadline pressure
    let daysUntilDeadline: number | undefined;
    if (deadline) {
      daysUntilDeadline = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysUntilDeadline < 3) {
        urgencyMultiplier *= 2.0;
        shouldDefer = false; // Override deferral if deadline is close
      }
      
      if (daysUntilDeadline < 1) {
        urgencyMultiplier *= 3.0;
        suggestedTone = 'urgent, direct';
      }
    }

    return {
      currentTime: now,
      dayOfWeek,
      timeOfDay,
      isWorkingHours,
      daysUntilDeadline,
      urgencyMultiplier,
      suggestedTone,
      shouldDefer,
      deferUntil
    };
  }

  /**
   * Analyze relational context with a contact
   */
  async analyzeRelationalContext(contactEmail: string): Promise<RelationalContext> {
    if (!this.userId) throw new Error('Context engine not initialized');

    // Get contact from database
    const contacts = await blink.db.contacts.list({
      where: {
        userId: this.userId,
        email: contactEmail
      },
      limit: 1
    });

    if (contacts.length === 0) {
      // Return default for new contact
      return this.getDefaultRelationalContext(contactEmail);
    }

    const contact = contacts[0];

    // Calculate relationship strength from engagement level
    const strengthMap: Record<string, number> = {
      'very_high': 1.0,
      'high': 0.8,
      'medium': 0.6,
      'low': 0.4,
      'very_low': 0.2
    };
    
    const strength = strengthMap[contact.engagementLevel || 'medium'] || 0.6;

    // Determine hierarchy from relationship type
    let hierarchy: RelationalContext['relationship']['hierarchy'] = 'peer';
    if (contact.relationshipType?.includes('client')) hierarchy = 'client';
    else if (contact.relationshipType?.includes('vendor')) hierarchy = 'vendor';
    else if (contact.relationshipType?.includes('superior')) hierarchy = 'superior';
    else if (contact.relationshipType?.includes('subordinate')) hierarchy = 'subordinate';

    // Calculate response expectation from relationship health
    let responseExpectation = 24; // hours
    if (contact.relationshipHealth === 'excellent') responseExpectation = 4;
    else if (contact.relationshipHealth === 'good') responseExpectation = 8;
    else if (contact.relationshipHealth === 'fair') responseExpectation = 24;
    else if (contact.relationshipHealth === 'poor') responseExpectation = 48;

    // Determine tone based on relationship and communication style
    let tone = 'professional';
    if (contact.communicationStyle === 'casual') tone = 'casual, friendly';
    else if (contact.communicationStyle === 'formal') tone = 'formal, concise';
    else if (contact.communicationStyle === 'technical') tone = 'technical, detailed';

    // Meeting priority based on relationship health and type
    let meetingPriority: RelationalContext['adaptiveBehavior']['meetingPriority'] = 'medium';
    if (hierarchy === 'superior' || hierarchy === 'client') meetingPriority = 'high';
    if (contact.relationshipHealth === 'excellent') meetingPriority = 'high';
    if (contact.relationshipHealth === 'poor') meetingPriority = 'critical';

    return {
      contactEmail,
      relationship: {
        strength,
        hierarchy,
        communicationFrequency: Number(contact.totalInteractions) || 0 / 52, // per week approximation
        responseExpectation,
        lastInteraction: contact.lastInteractionAt ? new Date(contact.lastInteractionAt) : new Date(),
        sentimentTrend: contact.relationshipHealth === 'excellent' ? 'positive' :
                       contact.relationshipHealth === 'poor' ? 'negative' : 'neutral',
        sharedProjects: [] // Would be populated from project tracking
      },
      adaptiveBehavior: {
        tone,
        meetingPriority,
        autoApproveReschedule: hierarchy === 'peer' && strength > 0.7,
        notificationPreference: meetingPriority === 'critical' ? 'immediate' : 'batched'
      }
    };
  }

  /**
   * Analyze project context
   */
  async analyzeProjectContext(): Promise<ProjectContext> {
    if (!this.userId) throw new Error('Context engine not initialized');

    const workingMemory = await selfLearningMemory.getWorkingMemory();

    const activeProjects = workingMemory.activeProjects.map(p => ({
      id: p.id,
      name: p.name,
      status: p.status === 'blocked' ? 'blocked' as const : 
              p.status === 'completed' ? 'completed' as const : 
              p.priority === 'critical' ? 'at_risk' as const : 'on_track' as const,
      deadline: p.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      dependencies: p.dependencies,
      criticalPath: p.priority === 'critical',
      healthScore: p.status === 'completed' ? 100 :
                   p.status === 'blocked' ? 20 :
                   p.priority === 'critical' ? 50 : 75
    }));

    // Build priority queue
    const priorityQueue = activeProjects
      .filter(p => p.status !== 'completed')
      .map(p => ({
        projectId: p.id,
        action: p.status === 'blocked' ? 'Unblock dependencies' : 'Continue work',
        dueBy: p.deadline,
        blocking: p.dependencies.filter(dep => 
          activeProjects.find(ap => ap.id === dep && ap.status === 'blocked')
        )
      }))
      .sort((a, b) => a.dueBy.getTime() - b.dueBy.getTime());

    return {
      activeProjects,
      projectLinks: {}, // Would be populated from email/meeting analysis
      priorityQueue
    };
  }

  /**
   * Analyze behavioral context - user's current state
   */
  async analyzeBehavioralContext(): Promise<BehavioralContext> {
    if (!this.userId) throw new Error('Context engine not initialized');

    // Get calendar data for workload analysis
    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const upcomingEvents = await blink.db.calendarEvents.list({
      where: { userId: this.userId },
      limit: 100
    });

    // Calculate meeting fatigue (meetings today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEvents = upcomingEvents.filter(e => {
      const eventDate = new Date(e.startTime);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === today.getTime();
    });

    const meetingsToday = todayEvents.length;
    const meetingFatigue = Math.min((meetingsToday / 6) * 100, 100); // 6+ meetings = 100% fatigue

    // Calculate focus time deficit
    const workingHours = 8 * 60; // 480 minutes
    const meetingMinutes = todayEvents.reduce((sum, e) => {
      const start = new Date(e.startTime);
      const end = new Date(e.endTime);
      return sum + (end.getTime() - start.getTime()) / (1000 * 60);
    }, 0);
    const focusTimeDeficit = Math.max(0, (workingHours * 0.5) - (workingHours - meetingMinutes));

    // Calculate workload for next 7 days
    const nextWeekEvents = upcomingEvents.filter(e => {
      const eventDate = new Date(e.startTime);
      return eventDate >= now && eventDate <= nextWeek;
    });

    const hoursScheduled = nextWeekEvents.reduce((sum, e) => {
      const start = new Date(e.startTime);
      const end = new Date(e.endTime);
      return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    }, 0);

    const availableHours = 7 * 8; // 7 days * 8 hours
    const workload = Math.min((hoursScheduled / availableHours) * 100, 100);
    const hoursOvercommitted = Math.max(0, hoursScheduled - availableHours);

    // Determine energy level based on time of day
    const hour = now.getHours();
    const semantic = await selfLearningMemory.getSemanticMemory();
    const peakHours = semantic.workingPatterns.peakProductivityHours;
    const lowHours = semantic.workingPatterns.lowEnergyHours;
    
    let energyLevel: 'high' | 'medium' | 'low' = 'medium';
    if (peakHours.includes(hour)) energyLevel = 'high';
    else if (lowHours.includes(hour)) energyLevel = 'low';

    // Determine stress level
    let stressLevel: 'low' | 'medium' | 'high' = 'low';
    if (workload > 85 || meetingFatigue > 70) stressLevel = 'high';
    else if (workload > 70 || meetingFatigue > 50) stressLevel = 'medium';

    // Generate protective actions
    const protectiveActions: string[] = [];
    if (meetingFatigue > 60) {
      protectiveActions.push('Decline non-critical meetings');
      protectiveActions.push('Defer email responses to tomorrow morning');
    }
    if (focusTimeDeficit > 120) {
      protectiveActions.push(`Block ${Math.ceil(focusTimeDeficit / 60)} hours of focus time tomorrow`);
    }
    if (hoursOvercommitted > 8) {
      protectiveActions.push('Suggest delegation for non-critical tasks');
      protectiveActions.push('Alert user about overcommitment');
    }

    // Generate recommendations
    const recommendations: string[] = [];
    if (energyLevel === 'low') {
      recommendations.push('Schedule important tasks for peak hours tomorrow');
    }
    if (stressLevel === 'high') {
      recommendations.push('Consider rescheduling some meetings');
      recommendations.push('Block recovery time for next week');
    }

    return {
      currentState: {
        workload,
        meetingFatigue,
        focusTimeDeficit,
        energyLevel,
        stressLevel,
        nextNDays: {
          days: 7,
          hoursOvercommitted,
          conflictsCount: 0 // Would be calculated from overlapping events
        }
      },
      protectiveActions,
      recommendations
    };
  }

  /**
   * Analyze environmental context
   */
  async analyzeEnvironmentalContext(): Promise<EnvironmentalContext> {
    // This would integrate with external APIs for company events, news, etc.
    // For now, return basic structure
    
    const now = new Date();
    const month = now.getMonth();
    const dayOfMonth = now.getDate();

    const seasonalFactors: string[] = [];
    
    // Quarter end approaching
    if ([2, 5, 8, 11].includes(month) && dayOfMonth > 20) {
      seasonalFactors.push('Quarter-end approaching - prioritize financial reviews');
    }

    // Year end
    if (month === 11 && dayOfMonth > 15) {
      seasonalFactors.push('Year-end approaching - focus on annual planning');
    }

    return {
      companyEvents: [],
      industryNews: [],
      marketConditions: [],
      seasonalFactors
    };
  }

  /**
   * Perform complete contextual assessment
   */
  async assessContext(params: {
    contactEmail?: string;
    deadline?: Date;
    isUrgent?: boolean;
    actionType: string;
  }): Promise<ContextualAssessment> {
    if (!this.userId) throw new Error('Context engine not initialized');

    // Gather all contextual dimensions
    const temporal = await this.analyzeTemporalContext(params.deadline, params.isUrgent);
    const relational = params.contactEmail 
      ? await this.analyzeRelationalContext(params.contactEmail)
      : undefined;
    const project = await this.analyzeProjectContext();
    const behavioral = await this.analyzeBehavioralContext();
    const environmental = await this.analyzeEnvironmentalContext();

    // Make overall recommendation using AI
    const contextSummary = {
      temporal: {
        shouldDefer: temporal.shouldDefer,
        urgencyMultiplier: temporal.urgencyMultiplier,
        daysUntilDeadline: temporal.daysUntilDeadline
      },
      relational: relational ? {
        priority: relational.adaptiveBehavior.meetingPriority,
        responseExpectation: relational.relationship.responseExpectation
      } : undefined,
      behavioral: {
        workload: behavioral.currentState.workload,
        stressLevel: behavioral.currentState.stressLevel,
        overcommitted: behavioral.currentState.nextNDays.hoursOvercommitted
      }
    };

    const { text } = await blink.ai.generateText({
      prompt: `Analyze this contextual situation and provide a recommendation:

Action Type: ${params.actionType}
Context: ${JSON.stringify(contextSummary, null, 2)}

Should we proceed with this action? Consider:
- Timing and urgency
- User's current workload and stress
- Relationship importance (if applicable)
- Alternative approaches

Provide: shouldProceed (true/false), confidence (0-1), reasoning (1-2 sentences), and 2-3 alternatives if not proceeding.`,
      maxTokens: 300
    });

    // Parse AI response
    const shouldProceed = text.toLowerCase().includes('should proceed: true') || 
                         text.toLowerCase().includes('yes, proceed');
    const confidence = 0.8; // Would parse from AI response
    const reasoning = text.substring(0, 200);
    const alternatives = temporal.shouldDefer 
      ? [`Defer until ${temporal.deferUntil?.toLocaleString()}`]
      : [];

    return {
      temporal,
      relational,
      project,
      behavioral,
      environmental,
      overallRecommendation: {
        shouldProceed,
        confidence,
        reasoning,
        alternatives
      }
    };
  }

  /**
   * Adjust action based on temporal context
   */
  adjustForTemporalContext(
    action: any,
    currentTime: Date
  ): any {
    const hour = currentTime.getHours();
    
    // Evening/night adjustments
    if (hour > 20 || hour < 6) {
      action.urgencyMultiplier = (action.urgencyMultiplier || 1.0) * 0.5;
      action.tone = 'friendly, non-work';
      if (!action.isUrgent) {
        action.defer = true;
        const tomorrow = new Date(currentTime);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);
        action.deferUntil = tomorrow;
      }
    }

    // Friday afternoon protection
    const dayOfWeek = currentTime.getDay();
    if (dayOfWeek === 5 && hour > 14 && !action.isUrgent) {
      action.defer = true;
      const monday = new Date(currentTime);
      monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7));
      monday.setHours(9, 0, 0, 0);
      action.deferUntil = monday;
    }

    return action;
  }

  private getDefaultRelationalContext(email: string): RelationalContext {
    return {
      contactEmail: email,
      relationship: {
        strength: 0.5,
        hierarchy: 'peer',
        communicationFrequency: 0,
        responseExpectation: 24,
        lastInteraction: new Date(),
        sentimentTrend: 'neutral',
        sharedProjects: []
      },
      adaptiveBehavior: {
        tone: 'professional',
        meetingPriority: 'medium',
        autoApproveReschedule: false,
        notificationPreference: 'batched'
      }
    };
  }
}

export const contextAwarenessEngine = new ContextAwarenessEngine();
